import { Component, Input, SimpleChanges } from '@angular/core';

@Component({
	selector: 'app-truck-fleet-usage-summary',
	templateUrl: './truck-fleet-usage-summary.component.html',
	styleUrls: ['./truck-fleet-usage-summary.component.css']
})
export class TruckFleetUsageSummaryComponent {
	@Input() data!: any;
	@Input() period!: string;
	@Input() selectedDays!: string;
	@Input() showLegend!: boolean;
	@Input() showFooterDate!: boolean;
	@Input() forDevice: boolean = false;
	@Input() forAnalytics: boolean = true;

	cards: any[] = [];
	today = new Date();
	showData: boolean = true;


	ngOnChanges(changes: SimpleChanges) {
		if(this.forAnalytics) {
			if(this.data && Object.keys(this.data).length) {
				this.buildCards('analytics');
			}

			if(changes['period'] || changes['selectedDays']) {
				this.buildCards('analytics');
			}
		} else {
			this.buildCards('dailyDetails');
		}
	}

	buildCards(type: string) {
		let selectedData;
		this.showData = true;
		if (type === 'analytics') {
			selectedData = this.data?.[this.period]?.[this.selectedDays];
		} else {
			selectedData = this.data?.[this.selectedDays];
		}

		if (!selectedData || (!selectedData.avgDailyUsage?.length && !selectedData.avgDailyAh?.length && !selectedData.maxTruckDailyHours?.length)) {
			this.showData = false;
			return;
		}

		const avgDailyUsage = selectedData.avgDailyUsage;
		const avgDailyAh = selectedData.avgDailyAh;

		if (!avgDailyUsage.length) 
			return;

		let totalAvgInUse = 0;
		let totalAvgIdle = 0;
		let totalAvgCharge = 0;

		const numOfTrucks = avgDailyUsage.length
		for(let data of avgDailyUsage) {
			const days = data.total_days_in_period || 1;

			const inUseHrs = data.inuse_time / 3600;
			const idleHrs = data.idle_time / 3600;
			const chargeHrs = data.charge_off_time_for_battery / 3600;

			totalAvgInUse += inUseHrs / days;
			totalAvgIdle += idleHrs / days;
			totalAvgCharge += chargeHrs / days;
		}
		const avgInUse = totalAvgInUse / numOfTrucks;
		const avgIdle = totalAvgIdle / numOfTrucks;
		const avgCharge = totalAvgCharge / numOfTrucks;

		const inUsePercent = (avgInUse * 100) / 24;
		const idlePercent = (avgIdle * 100) / 24;
		const chargePercent = (avgCharge * 100) / 24;

		/*Average Truck Fleet Daily Hours*/
		const avgCard = {
			title: 'Average Truck Fleet Daily Hours',
			class: 'card-blue',
			icon: 'fa fa-clock-o',
			iconClass: 'icon-blue',
			rows: [
				this.row('Average Truck In-Use Hours', avgInUse, inUsePercent, 'bg-blue'),
				this.row('Average Truck Idle Hours', avgIdle, idlePercent, 'bg-yellow'),
				this.row('Average Off/Charge Hours', avgCharge, chargePercent, 'bg-gray')
			]
		};


		let maxTruckDailyHours = selectedData.maxTruckDailyHours[0];

		/*Maximum Truck Fleet Daily Hours*/
		const maxCard = {
			title: 'Maximum Truck Fleet Daily Hours',
			class: 'card-orange',
			icon: 'fa fa-line-chart',
			iconClass: 'icon-orange',
			rows: [
				this.row('Max Truck In-Use Hours', maxTruckDailyHours?.maxInuse / 3600, ((maxTruckDailyHours?.maxInuse / 3600) * 100) / 24, 'bg-blue'),
				this.row('Max Truck Idle Hours', maxTruckDailyHours?.maxIdel / 3600, ((maxTruckDailyHours?.maxIdel / 3600) * 100) / 24, 'bg-yellow'),
				this.row('Max Off/Charge Hours', maxTruckDailyHours?.maxChargeOff / 3600, ((maxTruckDailyHours?.maxChargeOff / 3600) * 100) / 24, 'bg-gray'),
			]
		};

		let totalDailyAvailableAh = 0;
		let maxInUseAh = 0;
		let totalAvgInUseDailyAh = 0;
		for(let data of avgDailyAh) {
			const days = data.total_days_in_period || 1;
			const inUseHrs = data.inuse_ah / 3600;
			totalAvgInUseDailyAh += inUseHrs ? (inUseHrs / days) : 0;
			totalDailyAvailableAh += data.totalDailyAvailableAh;
			maxInUseAh += data?.max_inuse_ah
		}

		if (!isNaN(maxTruckDailyHours?.maxInUseAh))
			maxInUseAh = maxTruckDailyHours.maxInUseAh;

		const totalDailyAvailableAhPercent = totalDailyAvailableAh ? 100 : 0;
		const dailyinUseHrsPercent = totalDailyAvailableAh ? (totalAvgInUseDailyAh * 100) / totalDailyAvailableAh : 0;
		const maxInUseAhPercent = totalDailyAvailableAh ? ((maxInUseAh / 3600) * 100) / totalDailyAvailableAh : 0;
		let ahrCard = {
			title: 'Truck Fleet Daily Ah Usage',
			class: 'card-green',
			icon: 'fa fa-bolt',
			iconClass: 'icon-green',
			rows: [
				this.row('Average Truck In-Use Ah', totalAvgInUseDailyAh, dailyinUseHrsPercent, 'bg-blue', false),
				this.row('Max Truck In-Use Ah', (maxInUseAh / 3600), maxInUseAhPercent, 'bg-green', false),
				this.row('Max Available Ah', totalDailyAvailableAh, totalDailyAvailableAhPercent, 'bg-deep-orange', false),
			]
		};
		this.cards = [avgCard, ahrCard, maxCard];
	}

	row(label: string, value: number, percent: number, color: string, isHour: boolean = true) {
		return {
			label,
			value: isHour ? (value > 24 ? '24.0' : value.toFixed(1)) : Math.round(value).toLocaleString(),
			percent: percent > 100 ? '100.0' : percent.toFixed(1),
			color,
			isHour
		};
	}
}
